import React, { useRef, useState, useEffect } from 'react'
import { Checkbox, Col, Icon, Modal, notification, Row } from 'antd'
import { Formik } from 'formik'
import { useSelector } from 'react-redux'

import { ApplicationState } from 'src/store'
import { ColumnProps } from 'src/components/Ant/Table'
import {
  Id,
  Policy,
  PolicyFlowApproval,
  PolicyFlowFormat,
  PolicyFlowType,
  PolicyTree,
  PolicyApprovalRules
} from 'src/types'
import { Select } from 'src/components/Formik'
import { ToolIcon, Button, Table, Text } from 'src/components/Ant'
import { useT } from 'src/core/hooks/use-translation'
import * as PoliciesService from 'src/services/api/policies-flow'
import * as PoliciesTreeService from 'src/services/api/policies-trees'

import { validationSchema } from './validation-schema'
import * as Values from './values'
import sanitize from './sanitize'

import { PropsTreeSelected } from '../..'
import FlowApprovalsModal from '../../../flow/approvals/flow-approvals-modal'
import HierarchyFlowsApprovalsExceptionModal from '../hierarchy-flows-approvals-exception-modal'
import HierarchyFlowsBindExceptionStructure from '../hierarchy-flows-structure-exception-rule'

type Props = {
  policies: Policy[]
  policyTree: PolicyTree[]
  resetPoliciesTrees: () => void
  treesSelected: PropsTreeSelected
}

type PolicyFlowApprovalList = PolicyFlowApproval & { key: Id }

export default function HierarchyFlowsApprovals({
  policies,
  policyTree,
  resetPoliciesTrees,
  treesSelected
}: Props) {
  const { me } = useSelector((state: ApplicationState) => ({
    me: state.me
  }))

  const refFormik = useRef()
  const refFormikApprovalModal = useRef<Formik<PolicyFlowApproval>>(null)
  const refFormikExceptionModal = useRef<Formik<PolicyApprovalRules>>(null)
  const { t } = useT()

  const [loading, setLoading] = useState(false)
  const [listApprovalsRules, setListApprovalsRules] = useState<
    PolicyApprovalRules[]
  >([])
  const [dataSource, setDataSource] = useState<PolicyFlowApprovalList[]>([])
  const [selectedRule, setSelectedRule] = useState<PolicyApprovalRules>()
  const [modalPolicyFlowFormat, setModalPolicyFlowFormat] =
    useState<PolicyFlowFormat>(PolicyFlowFormat.STANDARD)
  const [modalPolicyFlowType, setModalPolicyFlowType] =
    useState<PolicyFlowType>(PolicyFlowType.HIERARCHY)

  function setModalPolicyFlow(record: PolicyFlowApproval): void {
    let type = PolicyFlowType.HIERARCHY
    let format = PolicyFlowFormat.STANDARD

    if (record.automatic_approval) {
      format = PolicyFlowFormat.AUTOMATIC
    }
    if (record.consensual_approval) {
      format = PolicyFlowFormat.CONSENSUS
    }

    if (record.cost_center_approval) {
      type = PolicyFlowType.COST_CENTER
    }
    if (record.reason_approval) {
      type = PolicyFlowType.REASON
    }
    setModalPolicyFlowFormat(format)
    setModalPolicyFlowType(type)
  }

  function UpdatePolicyTree() {
    PoliciesService.getRulesException({
      organization_id: me.organization.organization_id,
      policy_name: 'approval',
      policy_id: policyTree[0].policy_approval_id
    })
      .then(response => {
        if (response.status === 200) {
          setListApprovalsRules(response.data as PolicyApprovalRules[])
        } else {
          notification.error({
            message: t('common.response.status-500'),
            placement: 'bottomRight'
          })
        }
      })
      .catch(response => {
        notification.error({
          message: t('common.response.status-500'),
          placement: 'bottomRight'
        })
      })
      .finally(() => {
        setLoading(false)
      })
  }

  useEffect(() => {
    if (selectedRule) {
      setSelectedRule(
        listApprovalsRules.find(
          rule =>
            rule.policy_approval_rule_id ===
            selectedRule.policy_approval_rule_id
        )
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listApprovalsRules])

  useEffect(() => {
    setLoading(true)
    PoliciesService.getPolicyApprovals()
      .then(response => {
        if (response.status === 200) {
          const approvals: PolicyFlowApprovalList[] =
            response.data?.policy_approvals.map(data => ({
              ...data,
              key: data.policy_approval_id
            })) || []
          setDataSource(approvals)
        } else {
          notification.error({
            message: t('common.response.status-500'),
            placement: 'bottomRight'
          })
        }
      })
      .catch(response => {
        notification.error({
          message: t('common.response.status-500'),
          placement: 'bottomRight'
        })
      })
      .finally(() => {
        setLoading(false)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (policyTree?.length && policyTree[0].policy_approval_id) {
      setLoading(true)
      UpdatePolicyTree()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [policyTree])

  function LinkPolicyTree(value: any) {
    Modal.confirm({
      cancelText: t('common.actions.cancel'),
      content: t('web.tree.policies.modal.message'),
      onOk: () =>
        PoliciesTreeService.save({
          assign_tree_id: treesSelected.id,
          organization_id: me.organization.organization_id,
          policy: 'policy_approval_id',
          policy_id: Number(value.policy)
        })
          .then(response => {
            if (response.status === 200) {
              resetPoliciesTrees()
            } else {
              notification.error({
                message: t('common.response.status-500'),
                placement: 'bottomRight'
              })
            }
          })
          .catch(() => {
            notification.error({
              message: t('common.response.status-500'),
              placement: 'bottomRight'
            })
          })
    })
  }

  function UpdateActive(row: PolicyApprovalRules) {
    PoliciesService.SaveRulesException({
      organization_id: me.organization.organization_id,
      policy_approval_rules: [{ ...row, active: !row.active }],
      policy_id: row.policy_approval_id,
      policy_name: 'approval'
    })
      .then(response => {
        if (response.status === 200) {
          resetPoliciesTrees()
        } else {
          notification.error({
            message: t('common.response.status-500'),
            placement: 'bottomRight'
          })
        }
      })
      .catch(() => {
        notification.error({
          message: t('common.response.status-500'),
          placement: 'bottomRight'
        })
      })
  }

  const columns: ColumnProps<PolicyTree>[] = [
    {
      title: 'web.tree.flows.table.code',
      dataIndex: 'policy_approval.code',
      key: 'code'
    },
    {
      title: 'web.tree.flows.table.name',
      dataIndex: 'policy_approval.name',
      key: 'name'
    },
    {
      align: 'center',
      dataIndex: 'policy_approval',
      key: 'view',
      render: (record: PolicyFlowApproval) => (
        <Icon
          type="eye"
          onClick={() => {
            setModalPolicyFlow(record)
            refFormikApprovalModal.current.resetForm(record)
          }}
        />
      ),
      title: 'web.tree.flows.table.view'
    }
  ]

  const columnsRules: ColumnProps<PolicyApprovalRules>[] = [
    {
      title: 'web.tree.flows.table-rule.code',
      dataIndex: 'code',
      key: 'code'
    },
    {
      title: 'web.tree.flows.table-rule.name',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: 'web.tree.flows.table-rule.link',
      key: 'link',
      align: 'center',
      render: (row: PolicyApprovalRules) => (
        <Icon
          type="link"
          onClick={() => {
            setSelectedRule(row)
          }}
        />
      )
    },
    {
      title: 'web.tree.flows.table-rule.active',
      key: 'active',
      align: 'center',
      render: (row: PolicyApprovalRules) => (
        <Checkbox checked={row.active} onChange={() => UpdateActive(row)} />
      )
    },
    {
      title: 'web.tree.flows.table-rule.edit',
      key: 'edit',
      align: 'center',
      render: (row: PolicyApprovalRules) => (
        <Icon
          type="edit"
          onClick={() => {
            refFormikExceptionModal.current.resetForm(row)
          }}
        />
      )
    }
  ]

  function updateSelectedRule(update?: boolean): void {
    if (!update) {
      setSelectedRule(undefined)
      return
    }

    UpdatePolicyTree()
  }

  function handleApprovalsExceptionSubmit(values: PolicyApprovalRules): void {
    const policy_approval_rules = [sanitize(values)]
    setLoading(true)
    PoliciesService.SaveRulesException({
      organization_id: me.organization.organization_id,
      policy_id: policyTree?.[0]?.policy_approval.policy_approval_id,
      policy_approval_rules,
      policy_name: 'approval'
    })
      .then(response => {
        if (response.status === 200) {
          const record = response.data[0] as PolicyApprovalRules
          if (typeof values.policy_approval_rule_id === 'string') {
            setListApprovalsRules(list => ({ ...list, record }))
          } else {
            setListApprovalsRules(list =>
              list.map(item =>
                item.policy_approval_rule_id !== record.policy_approval_rule_id
                  ? item
                  : record
              )
            )
          }
          refFormikExceptionModal.current.resetForm(Values.initialValues)
        } else {
          notification.error({
            message: t('common.response.status-500'),
            placement: 'bottomRight'
          })
        }
      })
      .catch(response => {
        notification.error({
          message: t('common.response.status-500'),
          placement: 'bottomRight'
        })
      })
      .finally(() => {
        setLoading(false)
      })
  }

  return (
    <>
      <Formik
        initialValues={{ policy: '' }}
        onSubmit={LinkPolicyTree}
        ref={refFormik}
        render={formikProps => (
          <Row>
            <Col span={15}>
              <Select
                dataTestId="hierarchy-flows-approvals-flow-options"
                formikProps={formikProps}
                label={'web.tree.flows.approvals.flow-title'}
                name="policy"
                options={dataSource.map(approval => ({
                  value: `${approval.policy_approval_id}`,
                  label: approval.name,
                  title: approval.name
                }))}
                placeholder="common.actions.select"
                required={false}
              />
            </Col>
            <Col span={9}>
              <Button
                disabled={formikProps.values.policy === ''}
                type="default"
                onClick={() => {
                  const foundPolicy = dataSource.find(
                    policy =>
                      `${policy.policy_approval_id}` ===
                      `${formikProps.values.policy}`
                  )
                  if (foundPolicy) {
                    refFormikApprovalModal.current.resetForm(foundPolicy)
                    setModalPolicyFlow(foundPolicy)
                  }
                }}
                style={{ margin: '40px 10px 0px 10px' }}
              >
                <ToolIcon key="eye" title="web.receipts.card.view" type="eye" />
              </Button>

              <Button
                disabled={formikProps.values.policy === ''}
                onClick={() => {
                  formikProps.submitForm()
                }}
              >
                {t('web.tree.policies.link')}
              </Button>
            </Col>
          </Row>
        )}
      />

      <div>
        <Text style={{ fontSize: '25px', color: '#000' }} strong>
          {t('web.tree.flows.flow-link')}
        </Text>
        <Table
          className="table-striped"
          columns={columns}
          dataSource={policyTree?.[0]?.policy_approval_id ? policyTree : []}
          loading={loading}
          size="small"
        />
      </div>

      {policyTree?.[0]?.policy_approval.reason_approval && (
        <div>
          <Text style={{ fontSize: '25px', color: '#000' }} strong>
            {t('web.tree.flows.approvals.rules.title')}
          </Text>
          <Table
            dataSource={listApprovalsRules}
            loading={loading}
            className="table-striped"
            columns={columnsRules}
            size="small"
          />
          <div style={{ display: 'flex', justifyContent: 'end' }}>
            <Button
              type="primary"
              onClick={() => {
                refFormikExceptionModal.current.resetForm(
                  Values.getDefaultValues({
                    policy_approval_id:
                      policyTree?.[0]?.policy_approval.policy_approval_id,
                    organization_id: me.user.organization_id
                  })
                )
              }}
            >
              <ToolIcon type="plus" style={{ fontWeight: 'bold' }} />
              {t('web.tree.flows.approvals.rules.title')}
            </Button>
          </div>
        </div>
      )}
      <Formik
        initialValues={{} as any}
        onSubmit={undefined}
        ref={refFormikApprovalModal}
        render={formikProps => (
          <FlowApprovalsModal
            disabled
            formikProps={formikProps}
            isVisible={Boolean(formikProps?.values?.policy_approval_id)}
            modalPolicyFlowFormat={modalPolicyFlowFormat}
            modalPolicyFlowType={modalPolicyFlowType}
            refFormik={refFormikApprovalModal}
          />
        )}
      />
      <Formik
        initialValues={Values.initialValues}
        onSubmit={handleApprovalsExceptionSubmit}
        ref={refFormikExceptionModal}
        render={formikProps => (
          <HierarchyFlowsApprovalsExceptionModal
            formikProps={formikProps}
            policies={policies}
          />
        )}
        validationSchema={validationSchema}
      />
      <HierarchyFlowsBindExceptionStructure
        selectedRule={selectedRule}
        toggle={updateSelectedRule}
        treesSelected={treesSelected}
      />
    </>
  )
}
